import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search as SearchIcon, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { EntityDialog } from "@/components/EntityDialog";
import { yen, dateJP, num, area } from "@/lib/format";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLookups } from "@/context/LookupContext";
import { useI18n } from "@/context/I18nContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export function CrudPage({
  title, subtitle, endpoint, resource, columns, fields, testid,
  onView, onMessage, extraActions, transformSubmit, beforeSubmit, addLabel = "新規登録", searchKeys,
  onCreated, autoOpenNew, newInitial,
}) {
  const { hasWrite } = useAuth();
  const { nameOf, refresh: refreshLookups } = useLookups();
  const { t } = useI18n();
  const canWrite = hasWrite(resource);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newDefaults, setNewDefaults] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setRows(data);
    } catch (e) {
      if (!e.__handledAuth) toast.error("読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (autoOpenNew) { setEditing(null); setNewDefaults(newInitial || {}); setDialogOpen(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const helpers = { yen, dateJP, num, area, nameOf };

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const keys = searchKeys || columns.map((c) => c.key);
    return keys.some((k) => String(r[k] ?? "").toLowerCase().includes(q.toLowerCase()));
  });

  const save = async (payload) => {
    const body = transformSubmit ? transformSubmit(payload, editing) : payload;
    try {
      let created = null;
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, body);
        toast.success("更新しました");
      } else {
        const { data } = await api.post(endpoint, body);
        created = data;
        toast.success("登録しました");
      }
      await load();
      await refreshLookups();
      if (created && onCreated) onCreated(created);
    } catch (e) {
      if (!e.__handledAuth) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "保存に失敗しました");
      throw e;
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`${endpoint}/${deleting.id}`);
      toast.success("削除しました");
      setDeleting(null);
      await load();
      await refreshLookups();
    } catch (e) {
      if (!e.__handledAuth) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "削除に失敗しました");
    }
  };

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <div className="flex items-center gap-2">
            {typeof extraActions === "function" ? extraActions(load) : extraActions}
            {canWrite && (
              <Button
                onClick={() => { setEditing(null); setNewDefaults(null); setDialogOpen(true); }}
                className="bg-brand hover:bg-brand-hover text-white"
                data-testid={`add-${testid}`}
              >
                <Plus className="w-4 h-4 mr-1.5" />{t(addLabel)}
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-4 relative max-w-sm">
        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t("絞り込み検索...")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 bg-white"
          data-testid={`filter-${testid}`}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          helpers={helpers}
          canWrite={canWrite}
          onView={onView}
          onMessage={onMessage}
          onEdit={fields ? (row) => { setEditing(row); setDialogOpen(true); } : null}
          onDelete={fields ? (row) => setDeleting(row) : null}
          testid={testid}
        />
      )}

      {fields && (
        <EntityDialog
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setNewDefaults(null); }}
          title={editing ? t("編集") : t(addLabel)}
          fields={fields}
          initial={editing || newDefaults}
          onSubmit={save}
          beforeSubmit={beforeSubmit}
          testid={`dialog-${testid}`}
        />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("削除の確認")}</AlertDialogTitle>
            <AlertDialogDescription>{t("このデータを削除しますか？この操作は取り消せません。")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">{t("キャンセル")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm">{t("削除")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
